<script setup>
import {ref, nextTick, onMounted, onUnmounted, watch} from 'vue'
import {DateTime} from 'luxon'
import {__, __f} from '@/utils/i18n.js'
import icons from '@/utils/icons.js'
import {getWeekStart} from '@/utils/calendar.js'
import CalendarView from '@/components/CalendarView.vue'

// ── Emits ──────────────────────────────────────────────────────
const emit = defineEmits(['navigate-to-event', 'create-event', 'go-settings', 'edit-event'])

// ── Config ─────────────────────────────────────────────────────
const config = window.FcaEventsConfig || {}

// ── Refs ───────────────────────────────────────────────────────
const loginUrl = ref(config.loginUrl || config.portalBase + '?fcom_action=auth')
const isLoading = ref(true)
const errorMessage = ref(null)
const events = ref([])
const categories = ref([])
const searchQuery = ref('')
const selectedCats = ref([])
const isCatDropOpen = ref(false)
const catFilterRef = ref(null)
const filterMode = ref('upcoming')   // upcoming | past | saved | drafts
const viewMode = ref('grid')       // grid | list | calendar | week
const currentPage = ref(1)
const totalPages = ref(1)
const totalItems = ref(0)
const canCreate = ref(config.userPermissions?.can_create || false)
const searchInput = ref('')
const sortOrder = ref('asc')        // asc | desc

let searchDebounceTimer = null

// ── Settings ───────────────────────────────────────────────────
const settings = ref({
  week_start_day: 'monday',
  default_view: 'grid',
  items_per_page: 12,
  scroll_mode: 'pagination',
  date_format: 'D MMMM YYYY',
  time_format: '12h',
  show_summary_on_cards: true,
})

// ── Calendar refs ──────────────────────────────────────────────
const calendarEvents = ref([])
const calendarViewRef = ref(null)

// ── Space context ──────────────────────────────────────────────
const spaceContext = ref({
  isSpaceContext: false,
  spaceId: null,
  spaceTitle: null,
  spaceSlug: null,
  spaceData: null,
})

// ── Helpers ────────────────────────────────────────────────────

/**
 * Convert a numeric month string (e.g. "03") to a 3-letter abbreviation ("MAR").
 */
function formatMonthAbbrev(monthStr) {
  const month = parseInt(monthStr)
  if (isNaN(month) || month < 1 || month > 12) return monthStr
  const locale = (
      (window.FcaEventsConfig || {}).locale ||
      document.documentElement.lang ||
      'en'
  ).replace(/_/g, '-')
  const fmt = (window.FcaEventsConfig || {}).settings?.badge_month_format || 'MMM'
  return DateTime.local().set({month}).setLocale(locale).toFormat(fmt).toUpperCase()
}

/**
 * Return { start, end } ISO date strings for the calendar fetch range.
 * For month view: surrounds the month by one week on each side.
 * For week view: returns the exact 7-day window.
 */
function getCalendarDateRange(date, calView) {
  let start, end
  if (calView === 'month') {
    const firstOfMonth = date.startOf('month')
    const lastOfMonth = date.endOf('month')
    start = firstOfMonth.minus({weeks: 1}).toISODate()
    end = lastOfMonth.plus({weeks: 1}).toISODate()
  } else {
    const weekStart = getWeekStart(date, settings.value.week_start_day)
    const weekEnd = weekStart.plus({days: 6})
    start = weekStart.toISODate()
    end = weekEnd.toISODate()
  }
  return {start, end}
}

// ── API calls (raw fetch — mirrors original exactly) ────────────

async function fetchCalendarEvents(dateFrom, dateTo) {
  try {
    const params = new URLSearchParams()
    params.set('per_page', '500')
    params.set('date_from', dateFrom)
    params.set('date_to', dateTo)
    params.set('order', 'asc')
    if (filterMode.value === 'upcoming') params.set('event_status', 'upcoming')
    if (filterMode.value === 'past') params.set('event_status', 'past')
    if (filterMode.value === 'drafts') params.set('event_status', 'draft')
    if (selectedCats.value.length) params.set('category', selectedCats.value.join(','))
    if (searchQuery.value) params.set('s', searchQuery.value)
    if (spaceContext.value.isSpaceContext && spaceContext.value.spaceId) {
      params.set('space_id', spaceContext.value.spaceId)
    }

    const url = `${config.apiBase}events?${params}`
    const res = await fetch(url, {headers: {'X-WP-Nonce': config.nonce}})
    if (res.ok) {
      calendarEvents.value = await res.json()
    } else {
      console.error('Calendar events fetch failed:', res.status, res.statusText)
    }
  } catch (err) {
    console.error('Failed to fetch calendar events:', err)
  }
}

async function fetchSettings() {
  applySettingsFromConfig()
}

async function fetchCategories() {
  try {
    const res = await fetch(`${config.apiBase}categories`, {
      headers: {'X-WP-Nonce': config.nonce},
    })
    if (res.ok) categories.value = await res.json()
  } catch { /* ignore */
  }
}

async function fetchEvents() {
  isLoading.value = true
  errorMessage.value = null
  try {
    const params = new URLSearchParams()
    if (searchQuery.value) params.set('s', searchQuery.value)
    if (selectedCats.value.length) params.set('category', selectedCats.value.join(','))
    params.set('order', sortOrder.value)
    if (spaceContext.value.isSpaceContext && spaceContext.value.spaceId) {
      params.set('space_id', spaceContext.value.spaceId)
    }

    const perPage = settings.value.items_per_page || 12

    if (filterMode.value === 'saved') {
      const res = await fetch(`${config.apiBase}saved`, {
        headers: {'X-WP-Nonce': config.nonce},
      })
      if (!res.ok) {
        if (res.status === 401) {
          window.location.href = config.portalBase + '?fcom_action=auth&redirect_to=' + window.location.href
        } else {
          throw new Error(`HTTP ${res.status}`)
        }
      }
      events.value = await res.json()
      totalPages.value = 1
    } else {
      if (settings.value.scroll_mode === 'pagination') {
        params.set('page', String(currentPage.value))
        params.set('per_page', String(perPage))
      } else {
        params.set('per_page', String(perPage * 4))
      }
      if (filterMode.value === 'upcoming') params.set('event_status', 'upcoming')
      if (filterMode.value === 'past') params.set('event_status', 'past')
      if (filterMode.value === 'drafts') params.set('event_status', 'draft')

      const url = `${config.apiBase}events?${params}`
      const res = await fetch(url, {headers: {'X-WP-Nonce': config.nonce}})

      if (!res.ok) {
        if (res.status === 401) {
          window.location.href = config.portalBase + '?fcom_action=auth&redirect_to=' + window.location.href
        } else {
          throw new Error(`HTTP ${res.status}`)
        }
      }
      events.value = await res.json()
      totalItems.value = parseInt(res.headers.get('X-WP-Total') || '0')
      totalPages.value = parseInt(res.headers.get('X-WP-TotalPages') || '1')
    }
  } catch (err) {
    errorMessage.value = err.message
  } finally {
    isLoading.value = false
  }
}

async function toggleBookmark(event) {
  const url = event.is_saved ? `${config.apiBase}unsave` : `${config.apiBase}save`
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': config.nonce,
      },
      body: JSON.stringify({id: event.id}),
    })
    if (res.ok) event.is_saved = !event.is_saved
  } catch { /* ignore */
  }
}

// ── Event handlers ─────────────────────────────────────────────

function onEventClick(event) {
  if (event.status === 'draft') {
    emit('edit-event', event.slug, event.id)
  } else {
    emit('navigate-to-event', event.slug)
  }
}

function onCalendarSelectEvent(event) {
  if (event.status === 'draft') {
    emit('edit-event', event.slug, event.id)
  } else {
    emit('navigate-to-event', event.slug)
  }
}

// eslint-disable-next-line no-unused-vars
function onCalendarSelectDate(_date) {
  // Reserved for future date-click handling
}

function onCalendarViewChange(calView) {
  if (calendarViewRef.value) {
    const date = calendarViewRef.value.currentDate
    const range = getCalendarDateRange(date, calView)
    fetchCalendarEvents(range.start, range.end)
  }
}

function onCalendarDateChange(newDate) {
  const calView = calendarViewRef.value?.viewMode || 'month'
  const range = getCalendarDateRange(newDate, calView)
  fetchCalendarEvents(range.start, range.end)
}

function switchView(newView) {
  if (newView === viewMode.value) return
  viewMode.value = newView
  currentPage.value = 1
  if (newView === 'calendar' || newView === 'week') {
    const now = DateTime.now()
    const range = getCalendarDateRange(now, 'month')
    fetchCalendarEvents(range.start, range.end)
    // After render, sync with CalendarView's own current date/view
    nextTick(() => {
      if (calendarViewRef.value) {
        const date = calendarViewRef.value.currentDate || DateTime.now()
        const calView = calendarViewRef.value.viewMode || 'month'
        const range2 = getCalendarDateRange(date, calView)
        fetchCalendarEvents(range2.start, range2.end)
      }
    })
  } else {
    fetchEvents()
  }
}

function onSearchInput() {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    currentPage.value = 1
    fetchEvents()
  }, 350)
}

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  currentPage.value = 1
  fetchEvents()
}

function switchFilter(mode) {
  filterMode.value = mode
  currentPage.value = 1
  // Disable calendar view when switching to tabs that don't support it
  if (viewMode.value === 'calendar') viewMode.value = 'grid'
  fetchEvents()
}

function switchFilterUpcoming() {
  sortOrder.value = 'asc'
  switchFilter('upcoming')
}

function switchFilterPast() {
  sortOrder.value = 'desc'
  switchFilter('past')
}

// ── Space context detection ─────────────────────────────────────

function detectSpaceContext() {
  // Always check the current URL first — the global FcaSpaceContext may be stale
  // from a previous space navigation due to timing with the 50ms route watcher reset
  const match = window.location.pathname.match(/\/space\/([^/]+)\/events/)
  if (!match) {
    spaceContext.value.isSpaceContext = false
    spaceContext.value.spaceId = null
    spaceContext.value.spaceTitle = null
    spaceContext.value.spaceSlug = null
    spaceContext.value.spaceData = null
    return
  }
  // URL confirms we're in a space — use global context if available for richer data
  if (window.FcaSpaceContext && window.FcaSpaceContext.isSpaceContext) {
    spaceContext.value = {
      isSpaceContext: true,
      spaceId: window.FcaSpaceContext.spaceId,
      spaceTitle: window.FcaSpaceContext.spaceTitle,
      spaceSlug: window.FcaSpaceContext.spaceSlug || match[1],
      spaceData: window.FcaSpaceContext.spaceData,
    }
  } else {
    spaceContext.value.spaceSlug = match[1]
    spaceContext.value.isSpaceContext = true
  }
}

// ── "fca-event-saved" global event ─────────────────────────────

function applySettingsFromConfig() {
  const raw = { ...(window.FcaEventsConfig?.settings || {}) }
  if (raw.timezone_list) delete raw.timezone_list
  Object.assign(settings.value, raw)
  if (!new URL(window.location.href).searchParams.get('view')) {
    viewMode.value = raw.default_view || settings.value.default_view || 'grid'
  }
}

function onEventSavedGlobal() {
  setTimeout(() => {
    if (['calendar', 'week'].includes(viewMode.value)) {
      if (calendarViewRef.value) {
        const date = calendarViewRef.value.currentDate || DateTime.now()
        const calView = calendarViewRef.value.viewMode || 'month'
        const range = getCalendarDateRange(date, calView)
        fetchCalendarEvents(range.start, range.end)
      }
    } else {
      fetchEvents()
    }
  }, 300)
}

function onSettingsSavedGlobal() {
  applySettingsFromConfig()
  onEventSavedGlobal()
}

// ── Filter watcher: calendar/week modes reload on filter change ─

watch(filterMode, () => {
  if (['calendar', 'week'].includes(viewMode.value) && calendarViewRef.value) {
    const date = calendarViewRef.value.currentDate || DateTime.now()
    const calView = calendarViewRef.value.viewMode || 'month'
    const range = getCalendarDateRange(date, calView)
    fetchCalendarEvents(range.start, range.end)
  }
})

// ── Lifecycle ──────────────────────────────────────────────────

onMounted(async () => {
  // Detect space context, possibly fetching space data by slug
  detectSpaceContext()
  if (spaceContext.value.isSpaceContext && spaceContext.value.spaceSlug && !spaceContext.value.spaceId) {
    try {
      const res = await fetch(
          `${window.location.origin}/wp-json/fluent-community/v1/spaces/${spaceContext.value.spaceSlug}`,
          {headers: {'X-WP-Nonce': config.nonce}},
      )
      if (res.ok) {
        const space = await res.json()
        spaceContext.value.spaceId = space.id
        spaceContext.value.spaceTitle = space.title
        spaceContext.value.spaceData = space
      }
    } catch (err) {
      console.warn('[FCA Events] Failed to fetch space data:', err)
    }
  }

  // Set document title
  const siteTitle = window.fluentComAdmin?.site_title || document.title
  const menuLabel = config.settings?.header_menu_label || __('events')
  const contextLabel = spaceContext.value.isSpaceContext && spaceContext.value.spaceTitle
      ? `${spaceContext.value.spaceTitle} ${menuLabel}`
      : menuLabel
  document.title = contextLabel + ' - ' + siteTitle

  // Handle ?view= query param
  const urlView = new URL(window.location.href).searchParams.get('view')
  if (urlView && ['grid', 'list', 'calendar'].includes(urlView)) {
    viewMode.value = urlView
  }

  // Close category dropdown on outside click
  const handleOutsideClick = (e) => {
    if (catFilterRef.value && !catFilterRef.value.contains(e.target)) {
      isCatDropOpen.value = false
    }
  }
  document.addEventListener('click', handleOutsideClick)

  // Load settings and categories
  await fetchSettings()
  await fetchCategories()

  // Apply default view from settings if no ?view= param
  if (!new URL(window.location.href).searchParams.get('view')) {
    viewMode.value = (config.settings || {}).default_view || 'grid'
  }

  // Validate view mode
  if (!['grid', 'list', 'calendar', 'week'].includes(viewMode.value)) {
    viewMode.value = 'grid'
  }

  // Initial data load
  if (viewMode.value === 'calendar' || viewMode.value === 'week') {
    const now = DateTime.now()
    const range = getCalendarDateRange(now, viewMode.value === 'week' ? 'week' : 'month')
    await fetchCalendarEvents(range.start, range.end)
    isLoading.value = false
  } else {
    await fetchEvents()
  }

  window.addEventListener('fca-event-saved', onEventSavedGlobal)
  window.addEventListener('fca-events-settings-saved', onSettingsSavedGlobal)
})

onUnmounted(() => {
  window.removeEventListener('fca-event-saved', onEventSavedGlobal)
  window.removeEventListener('fca-events-settings-saved', onSettingsSavedGlobal)
})
</script>

<template>
  <div :aria-label="__('events')" class="fcom_events_addon" role="main">

    <!-- Skip link -->
    <a class="fcom_skip_link" href="#fca-events-content">{{ __('skip_to_content') }}</a>

    <!-- Space-context create button (shown above header when in a space) -->
    <div v-if="spaceContext.isSpaceContext && canCreate" class="fca-space-create-btn">
      <button
          :aria-label="__('create_event')"
          :title="__('create_event')"
          class="el-button fcom_primary_button"
          @click="$emit('create-event')"
      >
        <i :innerHTML="icons.plus" aria-hidden="true" class="fcom_icon"/>
        {{ __('create_event') }}
      </button>
    </div>

    <!-- Main header (hidden in space context) -->
    <header v-if="!spaceContext.isSpaceContext" class="fhr_content_layout_header">
      <h1 id="fca-events-heading" class="fhr_page_title">
        <i :innerHTML="icons.calendarFilled" aria-hidden="true" class="fcom_icon"/>
        {{ __('events') }}
      </h1>
      <div class="fhr_page_actions">
        <button
            v-if="config.userPermissions?.can_settings"
            :aria-label="__('settings')"
            :title="__('settings')"
            class="el-button fcom_settings_btn"
            @click="$emit('go-settings')"
        >
          <i :innerHTML="icons.settings" aria-hidden="true" class="fcom_icon"/>
        </button>
        <button
            v-if="canCreate"
            :aria-label="__('create_event')"
            :title="__('create_event')"
            class="el-button fcom_primary_button"
            @click="$emit('create-event')"
        >
          <i :innerHTML="icons.plus" aria-hidden="true" class="fcom_icon"/>
          {{ __('create') }}
        </button>
      </div>
    </header>

    <!-- Content region -->
    <div
        id="fca-events-content"
        :aria-label="__('event_listings')"
        class="fhr_content_layout_body"
        role="region"
    >
      <!-- Filter tabs -->
      <nav
          :aria-label="__('event_categories')"
          class="fcom_tabs"
          role="tablist"
      >
        <button
            id="tab-upcoming"
            :aria-selected="filterMode === 'upcoming'"
            :class="['fcom_tab', { active: filterMode === 'upcoming' }]"
            aria-controls="panel-upcoming"
            role="tab"
            @click="switchFilterUpcoming"
        >{{ __('upcoming') }}
        </button>

        <button
            id="tab-past"
            :aria-selected="filterMode === 'past'"
            :class="['fcom_tab', { active: filterMode === 'past' }]"
            aria-controls="panel-past"
            role="tab"
            @click="switchFilterPast"
        >{{ __('past') }}
        </button>

        <button
            id="tab-saved"
            :aria-selected="filterMode === 'saved'"
            :class="['fcom_tab', { active: filterMode === 'saved' }]"
            aria-controls="panel-saved"
            role="tab"
            @click="() => { filterMode = 'saved'; currentPage = 1; if (viewMode === 'calendar') viewMode = 'grid'; fetchEvents() }"
        >{{ __('saved') }}
        </button>

        <button
            v-if="canCreate"
            id="tab-drafts"
            :aria-selected="filterMode === 'drafts'"
            :class="['fcom_tab fcom_tab-draft', { active: filterMode === 'drafts' }]"
            aria-controls="panel-drafts"
            role="tab"
            @click="() => { filterMode = 'drafts'; currentPage = 1; if (viewMode === 'calendar') viewMode = 'grid'; fetchEvents() }"
        >{{ __('drafts') }}
        </button>
      </nav>

      <!-- Screen reader status -->
      <div aria-atomic="true" aria-live="polite" class="fcom_sr_only" role="status"></div>

      <!-- Toolbar: search + category filter + sort + view switcher -->
      <div class="fcom_toolbar_shell" role="search">
        <div class="fcom_toolbar">
          <div class="fcom_toolbar_main">
            <div class="fcom_search_wrap">
              <!-- Search -->
              <label class="fcom_sr_only" for="fca-event-search">{{ __('search_events') }}</label>
              <span class="fcom_search_icon">
              <i :innerHTML="icons.search" class="fcom_icon"/>
            </span>
              <input
                  id="fca-event-search"
                  v-model="searchQuery"
                  :aria-label="__('search_events')"
                  :placeholder="__('search_events')"
                  class="fcom_search_input"
                  type="search"
                  @input="onSearchInput"
              />
            </div>

            <!-- Category filter dropdown -->
            <div
                v-if="categories.length"
                ref="catFilterRef"
                class="fcom_cat_filter"
            >
              <button
                  :aria-label="__('filter_by_category')"
                  class="fcom_cat_filter_btn"
                  @click="isCatDropOpen = !isCatDropOpen"
              >
              <span>{{
                  selectedCats.length
                      ? selectedCats.length + ' ' + __('categories')
                      : __('all_categories')
                }}</span>
                <svg fill="currentColor" height="12" viewBox="0 0 12 12" width="12">
                  <path d="M6 8L1.5 3.5h9z"/>
                </svg>
              </button>

              <div v-if="isCatDropOpen" class="fcom_cat_filter_popover">
                <div class="fcom_cat_filter_header">
                  <span>{{ __('filter_by_category') }}</span>
                  <button
                      v-if="selectedCats.length"
                      class="fcom_cat_filter_clear"
                      @click="() => { selectedCats = []; isCatDropOpen = false; currentPage = 1; fetchEvents() }"
                  >{{ __('clear_all') }}
                  </button>
                </div>
                <div class="fcom_cat_filter_list">
                  <label
                      v-for="cat in categories"
                      :key="cat.id"
                      class="fcom_cat_filter_item"
                  >
                    <input
                        v-model="selectedCats"
                        :value="cat.id"
                        type="checkbox"
                        @change="() => { currentPage = 1; fetchEvents() }"
                    />
                    <span class="fcom_cat_filter_check"/>
                    <span>{{ cat.name }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Sort toggle -->
            <button
                :aria-label="sortOrder === 'asc' ? __('soonest_first') : __('latest_first')"
                :class="['fcom_sort_toggle', { 'is-desc': sortOrder === 'desc' }]"
                :title="sortOrder === 'asc' ? __('soonest_first') : __('latest_first')"
                @click="toggleSortOrder"
            >
              <svg class="fcom_sort_icon" fill="none" height="16" viewBox="0 0 16 16" width="16">
                <path class="fcom_sort_arrow_up" d="M8 3l3.5 4h-7L8 3z"/>
                <path class="fcom_sort_arrow_down" d="M8 13l-3.5-4h7L8 13z"/>
              </svg>
              <span class="fcom_sort_label">{{
                  sortOrder === 'asc' ? __('soonest_first') : __('latest_first')
                }}</span>
            </button>
          </div>

          <!-- View switcher -->
          <div
              :aria-label="__('change_view_layout')"
              class="fcom_view_switcher"
              role="group"
          >
            <button
                :aria-label="__('grid_view')"
                :aria-pressed="viewMode === 'grid'"
                :class="['fcom_view_btn', { active: viewMode === 'grid' }]"
                :innerHTML="icons.gridView"
                @click="switchView('grid')"
            />
            <button
                :aria-label="__('list_view')"
                :aria-pressed="viewMode === 'list'"
                :class="['fcom_view_btn', { active: viewMode === 'list' }]"
                :innerHTML="icons.listView"
                @click="switchView('list')"
            />
            <button
                :aria-label="__('calendar_view')"
                :aria-pressed="viewMode === 'calendar'"
                :class="['fcom_view_btn', { active: viewMode === 'calendar' }]"
                :innerHTML="icons.calendarView"
                @click="switchView('calendar')"
            />
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" aria-live="polite" role="status" style="text-align: center; padding: 60px 0">
        <div aria-hidden="true" class="fcom_spinner"/>
        <p style="margin-top: 12px; color: var(--fcom-text-off)">{{ __('loading') }}</p>
      </div>

      <!-- 401 / login required -->
      <div
          v-else-if="errorMessage && errorMessage.includes('401')"
          class="fcom_empty_state"
          role="alert"
      >
        <div class="fcom_empty_state-icon">
          <i :innerHTML="icons.lock" class="fcom_icon fcom_icon-xl"/>
        </div>
        <h3>{{ __('login_required') }}</h3>
        <p>{{ __('login_required_message') }}</p>
        <a
            :href="loginUrl"
            class="el-button fcom_primary_button"
            style="margin-top: 16px"
        >{{ __('login') }}</a>
      </div>

      <!-- Other error state -->
      <div v-else-if="errorMessage" class="fcom_alert fcom_alert_error" role="alert">
        {{ errorMessage }}
        <button
            class="el-button fcom_secondary_button el-button--small"
            style="margin-left: 12px; margin-right: 12px"
            @click="fetchEvents"
        >{{ __('retry') }}
        </button>
      </div>

      <!-- Empty state -->
      <div
          v-else-if="events.length === 0 && !['calendar', 'week'].includes(viewMode) && calendarEvents.length === 0"
          class="fcom_empty_state"
          role="status"
      >
        <div aria-hidden="true" class="fcom_empty_state-icon">
          <i :innerHTML="icons.calendarFilled" class="fcom_icon fcom_icon-xl"/>
        </div>
        <h3>{{ __('no_events_found') }}</h3>
        <p>{{ filterMode === 'saved' ? __('no_saved_events') : __('no_search_results') }}</p>
        <button
            v-if="canCreate && filterMode !== 'saved'"
            class="el-button fcom_primary_button"
            style="margin-top: 16px"
            @click="$emit('create-event')"
        >{{ __('create_an_event') }}
        </button>
      </div>

      <!-- Grid view -->
      <div v-if="viewMode === 'grid'" class="fcom_grid">
        <div
            v-for="event in events"
            :key="event.id"
            class="fcom_card"
            @click="event.status === 'draft' ? $emit('edit-event', event.slug, event.id) : $emit('navigate-to-event', event.slug)"
        >
          <!-- Card image / placeholder -->
          <div class="fcom_card-image">
            <img v-if="event.image" :alt="event.title" :src="event.image"/>
            <div v-else class="fcom_card-image-placeholder">
              <i :innerHTML="icons.calendarFilled" class="fcom_icon"/>
            </div>

            <!-- Date badge -->
            <div v-if="event.date_local" class="fcom_card-date-badge">
              <span class="fcom_card-date-month">{{ formatMonthAbbrev(event.date_local.slice(5, 7)) }}</span>
              <span class="fcom_card-date-day">{{ parseInt(event.date_local.slice(8, 10)) }}</span>
            </div>

            <!-- Bookmark button -->
            <button
                :class="['fcom_card-bookmark', { 'is-saved': event.is_saved }]"
                @click.stop="toggleBookmark(event)"
            >
              <i
                  :innerHTML="event.is_saved ? icons.bookmarkFilled : icons.bookmark"
                  class="fcom_icon"
              />
            </button>
          </div>

          <!-- Card body -->
          <div class="fcom_card-body">
            <div class="fcom_card-type-row">
              <span v-if="event.status === 'draft'" class="fcom_card-draft-badge">{{ __('draft') }}</span>
            </div>

            <h3 class="fcom_card-title">{{ event.title }}</h3>

            <div class="fcom_card-meta">
              <!-- Date -->
              <div v-if="event.date_display_mid || event.date_display" class="fcom_card-meta-row">
                <i :innerHTML="icons.calendarFilled" class="fcom_icon"/>
                <span>{{ event.date_display_mid || event.date_display }}</span>
              </div>
              <!-- Time -->
              <div v-if="event.time_display" class="fcom_card-meta-row">
                <i :innerHTML="icons.clockFilled" class="fcom_icon"/>
                <span>{{ event.time_display }}</span>
              </div>
              <!-- Location -->
              <div v-if="event.location || event.event_type === 'online'" class="fcom_card-meta-row">
                <i
                    :innerHTML="event.event_type === 'online' ? icons.monitorFilled : icons.locationFilled"
                    class="fcom_icon"
                />
                <span>{{ event.event_type === 'online' ? __('online') : event.location }}</span>
              </div>
            </div>

            <!-- Excerpt -->
            <p v-if="event.summary && settings.show_summary_on_cards !== false" class="fcom_card-excerpt">
              {{ event.summary }}
            </p>

            <!-- Categories -->
            <div v-if="event.categories && event.categories.length" class="fcom_card-categories">
              <span
                  v-for="cat in event.categories"
                  :key="cat.id"
                  :style="cat.color ? {
                  backgroundColor: cat.color + '20',
                  color:           cat.color,
                  borderColor:     cat.color + '40',
                } : {}"
                  class="fcom_category_tag"
              >{{ cat.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- List view -->
      <div v-else-if="viewMode === 'list'" class="fcom_list_view">
        <div
            v-for="event in events"
            :key="event.id"
            :class="['fcom_list_item', event.image ? 'fcom_list_item--has-image' : 'fcom_list_item--no-image']"
            @click="event.status === 'draft' ? $emit('edit-event', event.slug, event.id) : $emit('navigate-to-event', event.slug)"
        >
          <!-- Background image -->
          <div v-if="event.image" class="fcom_list_bg_image">
            <img :alt="event.title" :src="event.image" loading="lazy"/>
          </div>

          <!-- Static overlay -->
          <div class="fcom_list_overlay"/>

          <!-- Bookmark button -->
          <button
              :class="['fcom_list_item-bookmark', { 'is-saved': event.is_saved }]"
              :title="event.is_saved ? __('remove_from_saved') : __('save_event')"
              @click.stop="toggleBookmark(event)"
          >
            <i :innerHTML="event.is_saved ? icons.bookmarkFilled : icons.bookmark" class="fcom_icon"/>
          </button>

          <!-- Thumbnail (left column) -->
          <div class="fcom_list_image">
            <img v-if="event.image" :alt="event.title" :src="event.image"/>
            <div v-else class="fcom_list_image-placeholder">
              <i :innerHTML="icons.calendarFilled" class="fcom_icon"/>
            </div>
          </div>

          <!-- Content (right column) -->
          <div class="fcom_list_content">
            <div class="fcom_list_header">
              <h3 class="fcom_list_title">{{ event.title }}</h3>
              <span v-if="event.status === 'draft'" class="fcom_card-draft-badge">{{ __('draft') }}</span>
            </div>

            <div class="fcom_list_meta_row">
              <span v-if="event.date_display_short || event.date_display" class="fcom_list_meta_item">
                <i :innerHTML="icons.calendarFilled" class="fcom_icon"/>
                {{ event.date_display_short || event.date_display }}
              </span>
              <span v-if="event.date_display && event.time_display" class="fcom_list_meta_sep">·</span>
              <span v-if="event.time_display" class="fcom_list_meta_item">
                <i :innerHTML="icons.clockFilled" class="fcom_icon"/>
                {{ event.time_display }}
              </span>
            </div>

            <div v-if="event.location || event.event_type === 'online'" class="fcom_list_meta_row">
              <span class="fcom_list_meta_item">
                <i
                    :innerHTML="event.event_type === 'online' ? icons.monitorFilled : icons.locationFilled"
                    class="fcom_icon"
                />
                {{ event.event_type === 'online' ? __('online') : event.location }}
              </span>
            </div>

            <!-- Categories -->
            <div v-if="event.categories && event.categories.length" class="fcom_list_meta_row fcom_list_cats_row">
              <span
                  v-for="cat in event.categories"
                  :key="cat.id"
                  :style="cat.color ? {
                  backgroundColor: cat.color + '20',
                  color:           cat.color,
                  borderColor:     cat.color + '40',
                } : {}"
                  class="fcom_list_category_tag"
              >{{ cat.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar / Week view -->
      <CalendarView
          v-else-if="viewMode === 'calendar' || viewMode === 'week'"
          key="calendar"
          ref="calendarViewRef"
          :events="calendarEvents"
          :initial-view="viewMode === 'week' ? 'week' : 'month'"
          :week-start-day="settings.week_start_day || 'monday'"
          @select-event="onCalendarSelectEvent"
          @select-date="onCalendarSelectDate"
          @view-change="onCalendarViewChange"
          @date-change="onCalendarDateChange"
      />

      <!-- Pagination -->
      <div
          v-if="settings.scroll_mode === 'pagination' && totalPages > 1 && !['calendar', 'week'].includes(viewMode)"
          class="fcom_pagination"
      >
        <button
            :disabled="currentPage <= 1"
            class="fcom_page_btn"
            @click="() => { currentPage--; fetchEvents() }"
        >← {{ __('previous') }}
        </button>

        <span class="fcom_page_info">{{
            __f('page_x_of_y', {current: currentPage, total: totalPages})
          }}</span>

        <button
            :disabled="currentPage >= totalPages"
            class="fcom_page_btn"
            @click="() => { currentPage++; fetchEvents() }"
        >{{ __('next') }} →
        </button>
      </div>
    </div>
  </div>
</template>
